import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { toast } from "react-toastify";
import { Checkbox } from "../../../components/ui/checkbox";
import { useFileUploader } from "../../../BlobStorage/BlobServiceClient";
import { useParams } from "react-router-dom";
// @ts-ignore
import { useUserContext } from "../../../Contexts/UserProvider.tsx";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "../../../components/ui/sheet"
import { Loader2, ShoppingBag, Upload, X } from "lucide-react";
import { inputFieldValidator } from "../../../Utilities/validation";
import useApi from "../../Services/ServiceClient";
import { DynamicFieldRenderer } from "./DynamicFieldRenderer";
// @ts-ignore
import { ProductFormData } from "./Types.tsx";
import { productTypes } from "./mockdata";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Separator } from "../../../components/ui/separator";
import { Textarea } from "../../../components/ui/textarea";

interface AddProductDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  updateData: any;
  isUpdate: boolean;
  onSuccessCallBack: () => void;
}

const initialFormData: ProductFormData = {
  modelCode: "",
  productName: "",
  sku: "",
  productType: "",
  description: "",
  image: [],
  imageUrl: [],
  dynamicFields: [],
};

const AddProductDialog = ({ open, onOpenChange, updateData, isUpdate, onSuccessCallBack }: AddProductDrawerProps) => {
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [productDesign, setProductDesign] = useState([]);
  const [errors, setErrors] = useState({
    modelCode: false,
    productName: false,
    sku: false,
    productType: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getCatalogueTypes, createCatalougeItem, updateCatalougeItem } = useCatalogueManagement();
  const { tenant } = useUserContext();
  const { uploadFile } = useFileUploader();

  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    console.log("update itemDetails: ", updateData?.itemDetails);
    let fields = [];
    if (!updateData?.itemDetails || typeof updateData?.itemDetails !== "string") return;
    try {
      const parsed = JSON.parse(updateData?.itemDetails);
      const convertedFields = Object.values(parsed?.MetaDataFields || {});
      fields.push(...convertedFields)
    } catch (e) {
      console.error("Invalid itemDetails JSON:", updateData?.itemDetails);
    }


    if (isUpdate) {
      setImagePreview(updateData?.imageUrls[0] || null);
      setFormData({
        modelCode: updateData?.code,
        productName: updateData?.name,
        sku: updateData?.sku,
        productType: updateData?.itemCatalogId,
        description: updateData?.description,
        image: [],
        imageUrl: updateData?.imageUrls,
        dynamicFields: fields
      });
      setErrors({
        modelCode: false,
        productName: false,
        sku: false,
        productType: false,
      });
    }
  }, [isUpdate, updateData]);

  useEffect(() => {
    if (open && !isUpdate) {
      setFormData(initialFormData);
      setImagePreview(null);
      setErrors({
        modelCode: false,
        productName: false,
        sku: false,
        productType: false,
      });
    }
  }, [open, isUpdate]);

  useEffect(() => {
    getCatalogueTypes(tenant?.id).then((response) => {
      setProductDesign(response);
    }).catch((error) => {
      console.log("Error", error);
      toast.error("Failed to fetch Product Types");
    });
  }, []);

  // When product type changes, load the corresponding dynamic fields
  useEffect(() => {
    if (formData.productType) {
      const selectedType = productDesign.find((pt) => pt.id === formData.productType);
      if (selectedType) {
        // Deep clone the fields to avoid mutating the original data
        const clonedFields = JSON.parse(selectedType.fields);
        console.log("clonedFields : ", clonedFields);
        console.log("before clonedFields : ", formData.dynamicFields);

        const mergedFields = mergeFieldsWithUpdatedData(
          clonedFields?.metaDataFields,
          formData.dynamicFields
        );

        setFormData((prev) => ({
          ...prev,
          dynamicFields: mergedFields,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        dynamicFields: [],
      }));
    }
  }, [formData.productType]);

  const mergeFieldsWithUpdatedData = (clonedFields, currentFields) => {
    const currentMap = new Map(
      (currentFields || []).map((f) => [f.id, f])
    );

    return clonedFields.map((orig) => {
      const existing: any = currentMap.get(orig.id);

      return {
        ...orig,
        fieldData: orig.fieldData,
        fieldValue: existing ? existing.fieldValue : orig.fieldValue,
      };
    });
  };

  const errorBorderClass = (hasError: boolean) =>
    hasError ? "border-red-500 focus-visible:ring-red-500" : "border-border";

  const validateForm = () => {
    const newErrors = {
      modelCode: inputFieldValidator(formData.modelCode, "string"),
      productName: inputFieldValidator(formData.productName, "string"),
      sku: inputFieldValidator(formData.sku, "string"),
      productType: inputFieldValidator(formData.productType, "string"),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((v) => v);
  };

  const handleStaticFieldChange = (field: keyof ProductFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field in errors) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleDynamicFieldChange = (fieldId: string, value: string | string[] | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      dynamicFields: prev.dynamicFields.map((field) =>
        field.id === fieldId ? { ...field, fieldValue: value } : field
      ),
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file: any = e.target.files;
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields before saving");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Form Data:", formData);
      let tempImgNames = [];
      const images = Array.from(formData?.image || []);
      if (images?.length > 0) {
        await Promise.all(
          images?.map((item) => {
            return uploadFile("products", item).then((response) => {
              console.log(response);
              tempImgNames.push(response);
            }).catch((error) => {
              console.log(error);
            });
          })
        );
      }

      const data = {
        sku: formData.sku,
        name: formData.productName,
        code: formData.modelCode,
        description: formData.description,
        tags: [
          "string"
        ],
        reviews: [
          "strings"
        ],
        itemCatalogId: formData?.productType,
        imageUrls: [
          ...formData.imageUrl,
          ...tempImgNames
        ],
        itemCatalogName: productDesign.find((item) => item.id === formData.productType)?.catalogName,
        itemDetails: { MetaDataFields: { ...formData?.dynamicFields } }
      }

      console.log("final data : ", data);

      if (isUpdate) {
        await updateCatalougeItem({ id: updateData?.id, ...data })
        toast.success("Catalogue Item updated");
        onSuccessCallBack();

        setFormData(initialFormData);
        setImagePreview(null);
        onOpenChange(false);
        return;
      }

      await createCatalougeItem(data);
      toast.success("Catalogue Item created");
      onSuccessCallBack();
      setFormData(initialFormData);
      setImagePreview(null);
      onOpenChange(false);
    } catch (error) {
      console.log("Error : ", error);
      toast.error(isUpdate ? "Catalogue Item update failed" : "Catalogue Item creation failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setImagePreview(null);
    setErrors({
      modelCode: false,
      productName: false,
      sku: false,
      productType: false,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
        <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-bc detail-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <ShoppingBag className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold text-gray-900">Add New Product</SheetTitle>
                <p className="text-sm text-gray-600 mt-1">Add new product to your Product Catalogue</p>
              </div>
            </div>
          </div>

          <Button variant="outline" onClick={() => { onOpenChange(false); }} className="close-panel">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </Button>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-240px)]">
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Product Image</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData((prev) => ({ ...prev, image: null }));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-10 w-10 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-muted-foreground">
                        PNG, JPG (max 2MB)
                      </span>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Static Fields */}
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="modelCode" className="text-sm font-medium">
                  Model Code of the Item <span className="text-primary">*</span>
                </Label>
                <Input
                  id="modelCode"
                  value={formData.modelCode}
                  onChange={(e) => handleStaticFieldChange("modelCode", e.target.value)}
                  placeholder="Enter model code"
                  className={errorBorderClass(errors.modelCode)}
                />
                {errors.modelCode && (
                  <p className="text-xs text-red-500">Model code is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="productName" className="text-sm font-medium">
                  Product Name <span className="text-primary">*</span>
                </Label>
                <Input
                  id="productName"
                  value={formData.productName}
                  onChange={(e) => handleStaticFieldChange("productName", e.target.value)}
                  placeholder="Enter product name"
                  className={errorBorderClass(errors.productName)}
                />
                {errors.productName && (
                  <p className="text-xs text-red-500">Product name is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sku" className="text-sm font-medium">
                  SKU <span className="text-primary">*</span>
                </Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => handleStaticFieldChange("sku", e.target.value)}
                  placeholder="Enter SKU"
                  className={errorBorderClass(errors.sku)}
                />
                {errors.sku && (
                  <p className="text-xs text-red-500">SKU is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="productType" className="text-sm font-medium">
                  Product Design <span className="text-primary">*</span>
                </Label>
                <Select
                  value={formData.productType}
                  onValueChange={(value) => handleStaticFieldChange("productType", value)}
                >
                  <SelectTrigger className={errorBorderClass(errors.productType)}>
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    {productDesign.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.catalogName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.productType && (
                  <p className="text-xs text-red-500">Product design is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleStaticFieldChange("description", e.target.value)}
                  placeholder="Enter product description"
                  className="border-border min-h-[80px]"
                />
              </div>
            </div>

            {/* Dynamic Fields Section */}
            {formData.dynamicFields.length > 0 && (
              <>
                <Separator className="bg-border" />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    Additional Details
                  </h3>
                  <div className="grid gap-4">
                    {formData.dynamicFields.map((field) => (
                      <DynamicFieldRenderer
                        key={field.id}
                        field={field}
                        onChange={handleDynamicFieldChange}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </form>
        </ScrollArea>
        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="border-border"
          >
            Reset
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUpdate ? "Updating..." : "Adding..."}
              </>
            ) : (
              isUpdate ? "Update Product" : "Add Product"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddProductDialog;